/**
 * WebSocket-клиент для real-time push событий от backend.
 *
 * Архитектура:
 *  - Singleton-инстанс на всё приложение.
 *  - При connect(token) открывает соединение wss://.../api/v1/ws?token=...
 *  - Сокет у вкладки один. Обновлённый access-токен уходит в открытый сокет
 *    сообщением {"type":"auth","token":"…"} — без переподключения. Раньше
 *    каждое обновление токена (~14 мин) открывало новый сокет, а старый
 *    оставался жить: к вечеру вкладка держала десятки соединений и получала
 *    каждое событие десятки раз.
 *  - Сервер закрывает соединение кодом 4401, если токен истёк, а нового нет,
 *    или если токен отозван.
 *  - При обрыве — автоматический reconnect с exponential backoff
 *    (1с, 2с, 4с, 8с, 16с, далее 30с — стабильный потолок).
 *  - Принимает сообщения формата { event: string, ... }, диспатчит
 *    подписчикам по полю `event`.
 *  - Heartbeat: пинг от сервера каждые 30с автоматически отвечается
 *    pong-ом самим браузером — нам ничего делать не нужно.
 *  - Подписки на каналы (ТЗ Диска, П3): `watchChannels(['disk:folder:<id>'])`
 *    со счётчиком ссылок. Сокету уходят `subscribe` / `unsubscribe` только для
 *    первого и последнего наблюдателя канала; при каждом (пере)открытии сокета
 *    подписка на все активные каналы уходит заново — сервер новое соединение
 *    ничем не помнит.
 */

/** Изменение в папке Диска (контракт Д1, раздел 1 `DiskChange`). */
export interface DiskChangePayload {
  /** Глобальный номер события — по нему отбрасываются повторы. */
  seq: number;
  folder_id: string;
  /** Ревизия папки после изменения. */
  rev: number;
  node_id: string;
  kind:
    | 'created'
    | 'updated'
    | 'moved_in'
    | 'moved_out'
    | 'deleted'
    | 'restored'
    | 'version_added'
    | 'lock_changed'
    | 'acl_changed'
    | 'scan_result'
    | 'preview_ready';
  /** `op_id` изменяющего запроса: по нему окно узнаёт своё действие. */
  op_id: string | null;
  actor: { id: string; name: string } | null;
  at: string;
}

export type AgentEvent =
  | { event: 'ksp.inputs_changed' | 'ksp.calculation_completed' | 'ksp.snapshot_published' | 'ksp.period_plan_released' | 'ksp.milestone_alert_opened' | 'ksp.milestone_alert_closed' | 'ksp.delivery_failed'; project_id: string; object_id: string; event_id: string; at: string; version?: number }
  | { event: 'connected'; user_id: string }
  | {
      event: 'contract_analyzed';
      at: string;
      contract_id: string;
      version_id: string;
      risks_count: number;
      deviations_count: number;
    }
  | {
      event: 'contract_under_review';
      at: string;
      contract_id: string;
      version_id: string;
    }
  | {
      event: 'contract_analysis_progress';
      at: string;
      contract_id: string;
      version_id: string;
      /** started | thinking | window_done | validating | finalizing */
      stage: string;
      /** В chunked-режиме: текущее окно (1-based). null — single-pass. */
      window_current: number | null;
      /** В chunked-режиме: всего окон. null — single-pass. */
      window_total: number | null;
      /** Сколько рисков уже найдено (накопительно). */
      risks_so_far: number;
    }
  | { event: 'contract_deleted'; at: string; contract_id: string }
  | { event: 'contract_patched'; at: string; contract_id: string }
  | {
      event: 'risk_annotated';
      at: string;
      contract_id: string;
      risk_id: string;
      decision: string;
      actor_user_id: string;
    }
  | {
      event: 'deadline_created';
      at: string;
      deadline_id: string;
      scope: string;
      entity_id: string;
      kind: string;
      due_at: string;
    }
  | {
      event: 'deadline_notified';
      at: string;
      deadline_id: string;
      scope: string;
      entity_id: string;
    }
  | {
      event: 'deadline_overdue';
      at: string;
      deadline_id: string;
      scope: string;
      entity_id: string;
    }
  | {
      event: 'correspondence_received';
      at: string;
      correspondence_id: string;
      source: string;
      sender: string | null;
    }
  | {
      event: 'correspondence_classified';
      at: string;
      correspondence_id: string;
    }
  | {
      event: 'counterparty_refreshed';
      at: string;
      counterparty_id: string;
      status: string;
    }
  | {
      event: 'claim_under_review';
      at: string;
      claim_id: string;
      direction: string;
    }
  | {
      event: 'claim_analysis_progress';
      at: string;
      claim_id: string;
      /** preparing | thinking | validating | finalizing */
      stage: string;
      risks_so_far: number;
    }
  | {
      event: 'claim_analyzed';
      at: string;
      claim_id: string;
      risks_count: number;
    }
  | {
      event: 'claim_response_composed';
      at: string;
      claim_id: string;
      composed_id: string | null;
      template_id: string | null;
      fallback: boolean;
      /** Заполнен при fallback=true — причина почему DOCX не создан.
       *  Фронт показывает в карточке претензии как inline-баннер. */
      reason?: string | null;
    }
  | {
      event: 'contract_protocol_composed';
      at: string;
      contract_id: string;
      composed_id: string | null;
      template_id: string | null;
      fallback: boolean;
      reason?: string | null;
    }
  | {
      event: 'claim_deleted';
      at: string;
      claim_id: string;
    }
  | {
      event: 'composed_deleted';
      at: string;
      composed_id: string;
      claim_id: string | null;
      contract_id: string | null;
    }
  | {
      event: 'claim_risk_annotated';
      at: string;
      claim_id: string;
      risk_id: string;
      decision: string;
      actor_user_id: string;
    }
  | {
      event: 'lawsuit_composed';
      at: string;
      lawsuit_id: string;
      composed_id: string;
    }
  | {
      event: 'lawsuit_failed';
      at: string;
      lawsuit_id: string;
      error: string;
    }
  | {
      event: 'audit_session_updated';
      at: string;
      session_id: string;
      scope: string;
      entity_id: string;
      status: string;
    }
  | {
      event: 'entity_counterparties_extracted';
      at: string;
      /** 'contract' | 'claim' — единый источник для тега Parties. */
      entity_type: 'contract' | 'claim' | string;
      entity_id: string;
      count: number;
    }
  | {
      // Опубликовано обновление «Что нового» — фронт инвалидирует ленту
      // и показывает неблокирующую живую плашку (UpdatesBanner).
      // `kind` по контракту строка ('new' | 'improvement' | 'fix'), нормализуем
      // на приёме через normalizeKind из @entities/announcement.
      event: 'announcement_published';
      at: string;
      id: string;
      agent_code: string | null;
      kind: string;
      title: string;
    }
  | {
      // ОСНОВА.Кадры: уведомление пользователю (notify_user) — ответ
      // кандидата, новые кандидаты по заявке, тревога. Без ПДн: вид,
      // важность, заголовок; текст — запросом /agents/kadry/notifications.
      event: 'kadry_notification';
      at: string;
      id: string;
      kind: string;
      severity: string;
      title: string;
    }
  | {
      // Онлайн-статус пользователя изменился. Адресуется бэком ТОЛЬКО админам
      // (notify_user) — обычные сотрудники не получают чужие статусы.
      // online=true — открыта хотя бы одна живая вкладка (есть WS-соединение);
      // false — закрыта последняя. Initial-снапшот — поле is_online в /admin/users.
      event: 'presence_update';
      at: string;
      user_id: string;
      online: boolean;
    }
  | {
      // Сотруднику отдела адресно доставлен документ с рисками договора
      // (notify_user). Фронт инвалидирует ленту risk-notifications, а
      // RiskDispatchPopup всплывает карточкой «риски для вашего отдела».
      event: 'department_risk_dispatched';
      at: string;
      contract_id: string;
      department: string;
      target_id: string;
      focus_count: number;
    }
  | {
      // Рассылка рисков по отделам завершена (broadcast для карточки договора
      // у юриста — обновить историю рассылок).
      event: 'department_risks_dispatched';
      at: string;
      contract_id: string;
      dispatch_id: string;
      targets: number;
    }
  // ОСНОВА.Диск: изменение в папке, на канал которой подписано окно.
  | ({ event: 'disk.change' } & DiskChangePayload)
  // ОСНОВА.Диск: папка стала недоступна (права сняли или её удалили) — окно
  // уходит из неё и больше не получает её события.
  | { event: 'disk.access.lost'; folder_id: string; at: string };

export type ConnectionState = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

type Handler = (event: AgentEvent) => void;
type StateListener = (state: ConnectionState) => void;

/** Код закрытия: токен истёк без замены или отозван (см. backend `routes/ws.rs`). */
const CLOSE_AUTH = 4401;

export class RealtimeClient {
  private ws: WebSocket | null = null;
  /**
   * Номер текущего сокета. Обработчики сокета помнят свой номер: события и
   * закрытие замененного сокета больше ни на что не влияют (раньше `onclose`
   * старого сокета обнулял ссылку уже на новый).
   */
  private socketGen = 0;
  /** Токен, который сервер знает для текущего сокета. */
  private socketToken: string | null = null;
  private handlers = new Set<Handler>();
  private stateListeners = new Set<StateListener>();
  private currentState: ConnectionState = 'idle';
  private currentToken: string | null = null;
  private reconnectAttempt = 0;
  private reconnectTimer: number | null = null;
  // Если пользователь явно дисконнектился (logout) — не пытаемся
  // переподключаться даже при разрыве.
  private stopped = false;
  /** Активные каналы и число наблюдателей у каждого. */
  private channels = new Map<string, number>();

  connect(token: string): void {
    this.stopped = false;
    const ws = this.ws;
    if (this.currentToken === token && ws && ws.readyState <= WebSocket.OPEN) {
      // Уже подключены этим токеном — ничего не делаем.
      return;
    }
    this.currentToken = token;
    if (ws && ws.readyState === WebSocket.OPEN && this.sendAuth(ws, token)) {
      // Обновление токена: сокет тот же, сервер узнал новый токен.
      return;
    }
    if (ws && ws.readyState === WebSocket.CONNECTING) {
      // Сокет ещё открывается со старым токеном — новый уйдёт в onopen.
      return;
    }
    this.cancelReconnect();
    this.openSocket();
  }

  disconnect(): void {
    this.stopped = true;
    this.currentToken = null;
    this.cancelReconnect();
    this.dropSocket('client logout');
    this.setState('closed');
  }

  subscribe(handler: Handler): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  onState(listener: StateListener): () => void {
    this.stateListeners.add(listener);
    listener(this.currentState);
    return () => {
      this.stateListeners.delete(listener);
    };
  }

  getState(): ConnectionState {
    return this.currentState;
  }

  /**
   * Наблюдать за каналами (`disk:folder:<id>`). Возвращает отписку.
   *
   * У каждого канала счётчик наблюдателей: `subscribe` уходит серверу, когда
   * появляется первый, `unsubscribe` — когда уходит последний. Пока сокет не
   * открыт, ничего не шлётся: при открытии подписка на все активные каналы
   * уходит одним сообщением. Повторный вызов отписки ничего не делает — так
   * безопасно и в StrictMode, и при двойном размонтировании.
   */
  watchChannels(channels: readonly string[]): () => void {
    const unique = Array.from(new Set(channels.filter((c) => c.length > 0)));
    const added: string[] = [];
    for (const ch of unique) {
      const count = this.channels.get(ch) ?? 0;
      this.channels.set(ch, count + 1);
      if (count === 0) added.push(ch);
    }
    if (added.length > 0) this.sendControl({ type: 'subscribe', channels: added });

    let released = false;
    return () => {
      if (released) return;
      released = true;
      const removed: string[] = [];
      for (const ch of unique) {
        const count = this.channels.get(ch) ?? 0;
        if (count <= 1) {
          this.channels.delete(ch);
          if (count === 1) removed.push(ch);
        } else {
          this.channels.set(ch, count - 1);
        }
      }
      if (removed.length > 0) this.sendControl({ type: 'unsubscribe', channels: removed });
    };
  }

  /** Каналы, за которыми сейчас кто-то наблюдает. */
  activeChannels(): string[] {
    return Array.from(this.channels.keys());
  }

  /**
   * Служебное сообщение серверу в открытый сокет. Закрыт или открывается —
   * не шлём: подписка уйдёт целиком в `onopen`.
   */
  private sendControl(message: { type: 'subscribe' | 'unsubscribe'; channels: string[] }): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify(message));
    } catch (err) {
      console.warn('[ws] control send failed', err);
    }
  }

  private openSocket(): void {
    this.dropSocket('replaced');
    if (!this.currentToken) return;

    // Если access-токен уже истёк — НЕ открываем сокет. Иначе backend
    // отклонит upgrade с 401 (ws.rs валидирует токен до апгрейда), браузер
    // отдаст onclose code 1006, и мы уйдём в бесконечный reconnect тем же
    // мёртвым токеном (в проде это давало спам 401 в nginx-логах — токены,
    // истёкшие на часы). Ждём, пока владелец (RealtimeConnector) вызовет
    // connect() со свежим токеном после proactive/reactive refresh. В фоновой
    // вкладке это нормально: real-time push не нужен, соединение оживёт при
    // возврате (visibilitychange → refresh → новый accessToken → connect).
    if (isTokenExpired(this.currentToken)) {
      this.setState('reconnecting');
      return;
    }

    const token = this.currentToken;
    const wsUrl = this.buildUrl(token);
    this.setState(this.reconnectAttempt > 0 ? 'reconnecting' : 'connecting');

    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl);
    } catch (e) {
      console.warn('[ws] failed to construct WebSocket', e);
      this.scheduleReconnect();
      return;
    }
    const gen = ++this.socketGen;
    this.ws = ws;
    this.socketToken = token;

    ws.onopen = () => {
      if (gen !== this.socketGen) return;
      this.reconnectAttempt = 0;
      this.setState('open');
      // Токен обновился, пока сокет открывался.
      if (this.currentToken && this.currentToken !== this.socketToken) {
        this.sendAuth(ws, this.currentToken);
      }
      // Новое соединение сервер ни на что не подписал — повторяем подписку на
      // всё, за чем наблюдают окна (после обрыва, перезапуска, выкатки).
      if (this.channels.size > 0) {
        this.sendControl({ type: 'subscribe', channels: this.activeChannels() });
      }
    };

    ws.onmessage = (e) => {
      if (gen !== this.socketGen) return;
      try {
        const data = JSON.parse(e.data) as AgentEvent;
        for (const h of this.handlers) {
          try {
            h(data);
          } catch (err) {
            console.warn('[ws] handler error', err);
          }
        }
      } catch (err) {
        console.warn('[ws] bad payload', err);
      }
    };

    ws.onclose = (e) => {
      // Замененный сокет закрыли мы сами — текущий он не трогает.
      if (gen !== this.socketGen) return;
      this.ws = null;
      this.socketToken = null;
      if (this.stopped) {
        this.setState('closed');
        return;
      }
      // 1008/4401 — токен истёк или отозван. Со свежим токеном переподключаемся
      // сразу; с истёкшим ждём, пока владелец (RealtimeConnector) не вызовет
      // connect() с обновлённым.
      if (e.code === 1008 || e.code === CLOSE_AUTH) {
        if (this.currentToken && !isTokenExpired(this.currentToken)) {
          this.reconnectAttempt = 0;
          this.scheduleReconnect();
          return;
        }
        this.setState('closed');
        return;
      }
      this.scheduleReconnect();
    };

    ws.onerror = () => {
      // onerror не несёт деталей, реальная обработка — в onclose.
    };
  }

  /** Сообщить серверу новый токен в открытом сокете. false — не отправилось. */
  private sendAuth(ws: WebSocket, token: string): boolean {
    try {
      ws.send(JSON.stringify({ type: 'auth', token }));
      this.socketToken = token;
      return true;
    } catch (err) {
      console.warn('[ws] auth send failed', err);
      return false;
    }
  }

  /**
   * Закрыть текущий сокет так, чтобы его события и закрытие больше ни на что
   * не влияли: номер сокета сдвигается, обработчики сравнивают свой номер.
   */
  private dropSocket(reason: string): void {
    const ws = this.ws;
    if (!ws) return;
    this.ws = null;
    this.socketToken = null;
    this.socketGen += 1;
    try {
      ws.close(1000, reason);
    } catch {
      /* ignore */
    }
  }

  private cancelReconnect(): void {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.stopped || !this.currentToken) return;
    const delay = Math.min(30_000, 1000 * Math.pow(2, this.reconnectAttempt));
    this.reconnectAttempt += 1;
    this.setState('reconnecting');
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
    }
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, delay);
  }

  private buildUrl(token: string): string {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const t = encodeURIComponent(token);
    return `${proto}//${host}/api/v1/ws?token=${t}`;
  }

  private setState(s: ConnectionState): void {
    if (this.currentState === s) return;
    this.currentState = s;
    for (const l of this.stateListeners) {
      try {
        l(s);
      } catch (err) {
        console.warn('[ws] state listener error', err);
      }
    }
  }
}

/**
 * true, если JWT истёк по полю `exp`. Подпись НЕ проверяется — нам нужно
 * лишь не открывать заведомо отклоняемое соединение. При любой ошибке
 * разбора возвращаем false (не блокируем — пусть решает сервер).
 *
 * Дублирует логику `features/auth/model/refresh.getTokenExpiryMs`, но
 * специально не импортирует её: `shared/` по Feature-Sliced Design не
 * должен зависеть от `features/`.
 */
function isTokenExpired(token: string): boolean {
  try {
    const payload = token.split('.')[1];
    if (!payload) return false;
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
    const claims = JSON.parse(atob(padded)) as { exp?: number };
    if (typeof claims.exp !== 'number') return false;
    return claims.exp * 1000 <= Date.now();
  } catch {
    return false;
  }
}

export const realtime = new RealtimeClient();
